/**
 * GroupDetailsDialog Component
 * 
 * Dialog for managing group details (add, edit, remove, reorder).
 */

import React, { ReactElement } from "react";
import {
  Box,
  Paper,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import StandardDialog from "../../../components/StandardDialog";
import DeleteIconButton from "../../../components/DeleteIconButton";
import { GroupDetailsContent } from "./GroupDetailsContent";
import { TransactionCategoryDetail, TransactionCategory, TransactionCategoryGroup, Transaction } from "../../../types";
import { DragEndEvent } from "@dnd-kit/core";

export interface SortableDetailCardProps {
  detail: TransactionCategoryDetail;
  index: number;
  categoryName: string;
  isDetailUsed: boolean;
  onEditDetail: (index: number) => void;
  onRemoveDetail: (index: number) => void;
  editingDetailIndex: number | null;
}

export function SortableDetailCard({
  detail,
  index,
  categoryName,
  isDetailUsed,
  onEditDetail,
  onRemoveDetail,
  editingDetailIndex,
}: SortableDetailCardProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: String(index),
    disabled: editingDetailIndex !== null,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditing = editingDetailIndex !== null;
  const isThisDetailEditing = editingDetailIndex === index;
  const isOtherDetailEditing = isEditing && !isThisDetailEditing;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: 1,
        border: 2,
        borderColor: isThisDetailEditing ? "primary.main" : "divider",
        borderRadius: 1,
        backgroundColor: isThisDetailEditing 
          ? (theme) => theme.palette.mode === "dark" 
            ? "rgba(25, 118, 210, 0.2)"
            : "rgba(25, 118, 210, 0.08)"
          : "background.paper",
        "&:hover": { boxShadow: 2 },
        transition: "all 0.2s",
        cursor: isDragging ? "grabbing" : "default",
        opacity: isOtherDetailEditing ? 0.6 : 1,
        boxShadow: isThisDetailEditing ? 2 : 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <IconButton
          {...(isOtherDetailEditing ? {} : attributes)}
          {...(isOtherDetailEditing ? {} : listeners)}
          size="small"
          disabled={isOtherDetailEditing}
          sx={{
            cursor: isOtherDetailEditing ? "not-allowed" : "grab",
            "&:active": { cursor: isOtherDetailEditing ? "not-allowed" : "grabbing" },
            color: isOtherDetailEditing ? "action.disabled" : "text.secondary",
            p: 0.5,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {detail.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Type: {detail.type === "list" ? "List" : "Measurable"}
          </Typography>
          {detail.type === "list" && detail.listItems && detail.listItems.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              Items: {detail.listItems.length}
            </Typography>
          )}
          {detail.type === "measurable" && detail.unit && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              Unit: {detail.unit}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => onEditDetail(index)}
            disabled={isOtherDetailEditing}
            sx={{ 
              color: isOtherDetailEditing ? "action.disabled" : "primary.main",
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <DeleteIconButton
            onClick={() => onRemoveDetail(index)}
            disabled={isDetailUsed || isEditing}
            disabledTooltip={
              isEditing
                ? "Finish or cancel editing to delete details"
                : `Cannot delete detail "${detail.name}": It is used in one or more transactions`
            }
            size="small"
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

interface GroupDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  groupName: string;
  editingGroupDetails: TransactionCategoryDetail[];
  setEditingGroupDetails: (details: TransactionCategoryDetail[]) => void;
  originalGroupDetails: TransactionCategoryDetail[];
  newDetailName: string;
  setNewDetailName: (name: string) => void;
  newDetailType: "list" | "measurable" | "";
  setNewDetailType: (type: "list" | "measurable" | "") => void;
  newDetailUnit: string;
  setNewDetailUnit: (unit: string) => void;
  newListItems: string[];
  setNewListItems: (items: string[]) => void;
  detailNameError: string;
  setDetailNameError: (error: string) => void;
  editingDetailIndex: number | null;
  setEditingDetailIndex: (index: number | null) => void;
  editingOriginalDetail: TransactionCategoryDetail | null;
  setEditingOriginalDetail: (detail: TransactionCategoryDetail | null) => void;
  savingDetails: boolean;
  error: string;
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  transactions: Transaction[];
  isGroupDetailUsed: (groupName: string, detailName: string) => boolean;
  hasGroupDetailsChanged: (
    editingGroupDetails: TransactionCategoryDetail[],
    originalGroupDetails: TransactionCategoryDetail[]
  ) => boolean;
  hasPartialFormData: (
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[],
    editingDetailIndex: number | null
  ) => boolean;
  hasEditedDetailChanged: (
    editingDetailIndex: number | null,
    editingOriginalDetail: TransactionCategoryDetail | null,
    newDetailName: string,
    newDetailType: "list" | "measurable" | "",
    newDetailUnit: string,
    newListItems: string[]
  ) => boolean;
  onDragEnd: (event: DragEndEvent) => void;
  onEditDetail: (index: number) => void;
  onRemoveDetail: (index: number) => void;
}

/**
 * Dialog for managing group details
 */
function GroupDetailsDialog({
  open,
  onClose,
  onConfirm,
  groupName,
  editingGroupDetails,
  setEditingGroupDetails,
  originalGroupDetails,
  newDetailName,
  setNewDetailName,
  newDetailType,
  setNewDetailType,
  newDetailUnit,
  setNewDetailUnit,
  newListItems,
  setNewListItems,
  detailNameError,
  setDetailNameError,
  editingDetailIndex,
  setEditingDetailIndex,
  editingOriginalDetail,
  setEditingOriginalDetail,
  savingDetails,
  error,
  categories,
  groups,
  transactions,
  isGroupDetailUsed,
  hasGroupDetailsChanged,
  hasPartialFormData,
  hasEditedDetailChanged,
  onDragEnd,
  onEditDetail,
  onRemoveDetail,
}: GroupDetailsDialogProps): ReactElement {
  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={`Manage Details - ${groupName}`}
      confirmText={savingDetails ? "Saving..." : "Save"}
      onConfirm={onConfirm}
      confirmDisabled={savingDetails || !hasGroupDetailsChanged(editingGroupDetails, originalGroupDetails) || editingDetailIndex !== null || hasPartialFormData(newDetailName, newDetailType, newDetailUnit, newListItems, editingDetailIndex)}
      maxWidth="sm"
      alert={error ? { severity: "error", message: error } : undefined}
      paperSx={{ mt: { xs: 2.5, sm: 0 } }}
    >
      <GroupDetailsContent
        groupName={groupName}
        editingGroupDetails={editingGroupDetails}
        setEditingGroupDetails={setEditingGroupDetails}
        newDetailName={newDetailName}
        setNewDetailName={setNewDetailName}
        newDetailType={newDetailType}
        setNewDetailType={setNewDetailType}
        newDetailUnit={newDetailUnit}
        setNewDetailUnit={setNewDetailUnit}
        newListItems={newListItems}
        setNewListItems={setNewListItems}
        detailNameError={detailNameError}
        setDetailNameError={setDetailNameError}
        editingDetailIndex={editingDetailIndex}
        setEditingDetailIndex={setEditingDetailIndex}
        editingOriginalDetail={editingOriginalDetail}
        setEditingOriginalDetail={setEditingOriginalDetail}
        savingDetails={savingDetails}
        categories={categories}
        groups={groups}
        transactions={transactions}
        isGroupDetailUsed={isGroupDetailUsed}
        hasPartialFormData={hasPartialFormData}
        hasEditedDetailChanged={hasEditedDetailChanged}
        onDragEnd={onDragEnd}
        onEditDetail={onEditDetail}
        onRemoveDetail={onRemoveDetail}
      />
    </StandardDialog>
  );
}

export default GroupDetailsDialog;
